import aaicLogoPositive from "@workspace/logos/aaic-horizontal";
import aaicLogoNegative from "@workspace/logos/aaic-horizontal-negative";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuGroup,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuTrigger,
} from "@workspace/ui/components/dropdown-menu";
import { cn } from "@workspace/ui/lib/utils";
import { LogIn, LogOut, Menu, Moon, Sun } from "lucide-react";
import type { ReactNode } from "react";
import { NavLink, useLocation } from "react-router";

import { useTheme } from "../theme";
import { Button } from "./button";

export type HeaderUser = { name: string } | null;
export type HeaderNavigationLink = { label: string; to: string; end?: boolean };
export type HeaderNavigationItem =
	| HeaderNavigationLink
	| { label: string; items: readonly HeaderNavigationLink[] };
const defaultNavigation: readonly HeaderNavigationItem[] = [
	{ label: "产品介绍", to: "/", end: true },
];

export function AppHeader({
	product: _product,
	descriptor: _descriptor,
	accent: _accent,
	user,
	onLogout,
	actions,
	navigation = defaultNavigation,
	overlay = false,
	branding,
}: {
	product: string;
	descriptor: string;
	accent: string;
	user: HeaderUser;
	onLogout?: () => void;
	actions?: ReactNode;
	navigation?: readonly HeaderNavigationItem[];
	overlay?: boolean;
	branding?: { light: string; dark: string; alt: string };
}) {
	const { theme, toggleTheme } = useTheme();
	const aaicLogo =
		overlay || theme === "dark"
			? (branding?.dark ?? aaicLogoNegative)
			: (branding?.light ?? aaicLogoPositive);
	const location = useLocation();
	const isGroup = (
		item: HeaderNavigationItem,
	): item is Extract<
		HeaderNavigationItem,
		{ items: readonly HeaderNavigationLink[] }
	> => "items" in item;
	return (
		<header
			className={cn(
				"sticky top-0 z-40 h-20 border-b shadow-sm shadow-primary/5 backdrop-blur-2xl",
				overlay
					? "border-sky-200/15 bg-[#07152d]/94 text-white shadow-[0_10px_36px_rgb(2_12_35/35%)]"
					: "bg-background/82",
			)}
		>
			<div className="mx-auto flex h-20 max-w-[1680px] items-center gap-4 px-5 lg:px-8">
				<NavLink className="flex shrink-0 items-center" to="/">
					<img
						alt={branding?.alt ?? "北京语言大学智能媒体与大数据创新中心"}
						className="h-8 w-auto shrink-0 sm:h-9"
						src={aaicLogo}
					/>
				</NavLink>
				<nav
					className="ml-auto hidden min-w-0 items-center gap-1 xl:flex"
					aria-label="主导航"
				>
					{navigation.map((item) =>
						isGroup(item) ? (
							<DropdownMenu key={item.label}>
								<DropdownMenuTrigger
									render={
										<Button
											className={cn(
												"whitespace-nowrap text-sm",
												item.items.some((link) =>
													location.pathname.startsWith(link.to),
												) && "bg-accent text-accent-foreground",
											)}
											size="sm"
											variant="ghost"
										/>
									}
								>
									{item.label}
								</DropdownMenuTrigger>
								<DropdownMenuContent align="start" className="w-52">
									<DropdownMenuGroup>
										<DropdownMenuLabel>{item.label}</DropdownMenuLabel>
										{item.items.map((link) => (
											<DropdownMenuItem
												key={link.to}
												render={<NavLink end={link.end} to={link.to} />}
											>
												{link.label}
											</DropdownMenuItem>
										))}
									</DropdownMenuGroup>
								</DropdownMenuContent>
							</DropdownMenu>
						) : (
							<NavLink
								className={({ isActive }) =>
									cn(
										"whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-[color,background-color,transform] hover:-translate-y-px",
										isActive
											? overlay
												? "bg-sky-300/18 text-sky-100 shadow-[inset_0_0_0_1px_rgb(125_211_252/22%)]"
												: "bg-accent text-accent-foreground"
											: overlay
												? "text-sky-100/72 hover:bg-sky-200/10 hover:text-white"
												: "text-muted-foreground hover:text-foreground",
									)
								}
								end={item.end}
								key={item.to}
								to={item.to}
							>
								{item.label}
							</NavLink>
						),
					)}
				</nav>
				<DropdownMenu>
					<DropdownMenuTrigger
						className="ml-auto xl:hidden"
						render={
							<Button aria-label="打开产品导航" size="icon" variant="outline" />
						}
					>
						<Menu />
					</DropdownMenuTrigger>
					<DropdownMenuContent
						align="end"
						className="max-h-[calc(100dvh-6rem)] w-56 overflow-y-auto"
					>
						{navigation.map((item) =>
							isGroup(item) ? (
								<DropdownMenuGroup key={item.label}>
									<DropdownMenuLabel>{item.label}</DropdownMenuLabel>
									{item.items.map((link) => (
										<DropdownMenuItem
											key={link.to}
											render={<NavLink end={link.end} to={link.to} />}
										>
											{link.label}
										</DropdownMenuItem>
									))}
								</DropdownMenuGroup>
							) : (
								<DropdownMenuItem
									key={item.to}
									render={<NavLink end={item.end} to={item.to} />}
								>
									{item.label}
								</DropdownMenuItem>
							),
						)}
						<DropdownMenuGroup>
							<DropdownMenuLabel>账户</DropdownMenuLabel>
							{user ? (
								<DropdownMenuItem onClick={onLogout}>
									<LogOut />
									退出
								</DropdownMenuItem>
							) : (
								<DropdownMenuItem render={<NavLink to="/login" />}>
									<LogIn />
									登录
								</DropdownMenuItem>
							)}
						</DropdownMenuGroup>
					</DropdownMenuContent>
				</DropdownMenu>
				<div className="flex min-w-0 items-center gap-2">{actions}</div>
				<Button
					aria-label="切换明暗模式"
					className={
						overlay
							? "text-sky-100 hover:bg-sky-100/12 hover:text-white"
							: undefined
					}
					onClick={toggleTheme}
					size="icon"
					variant="ghost"
				>
					{theme === "dark" ? <Sun /> : <Moon />}
				</Button>
				{user ? (
					<Button
						className={cn(
							"hidden xl:inline-flex",
							overlay
								? "border-sky-200/30 bg-sky-100/10 text-white hover:bg-sky-100/20 hover:text-white"
								: undefined,
						)}
						onClick={onLogout}
						variant="outline"
					>
						<LogOut data-icon="inline-start" />
						退出
					</Button>
				) : (
					<Button
						className="hidden xl:inline-flex"
						nativeButton={false}
						render={<NavLink to="/login" />}
					>
						<LogIn data-icon="inline-start" />
						登录
					</Button>
				)}
			</div>
		</header>
	);
}
